/**
 * auth-context.tsx — Estado global de autenticação.
 *
 * Ao montar, consulta GET /api/auth/me para descobrir se há sessão ativa.
 * Expõe `usuario`, `carregando` e as ações `login` / `logout`.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '@/lib/api';

/** Uma loja como item de menu (`/me`). */
export interface LojaMenu {
  id: number;
  codigo: string;
  nome: string;
  nome_curto?: string | null;
}

export interface Usuario {
  id: number;
  nome: string;
  usuario: string;
  email?: string | null;
  admin: boolean;
  autorizador?: boolean;
  perfil?: string | null;
  perfil_nome?: string | null;
  foto?: string | null;
  /** @deprecated Modelo antigo (perfil → seção). Sai na Fase 5 da frente `acessos`. */
  secoes?: string[];
  /** As permissões `modulo.recurso.verbo` que esta pessoa alcança. */
  permissoes?: string[];
  loja_id?: number | null;
  /** As lojas cuja lista de OS a pessoa abre — um item de menu por loja.
   *  Vem do âmbito (migration 126), não da loja padrão. */
  lojas_os?: LojaMenu[];
  /** O mesmo para as duas telas da agenda (29/10/2026). */
  lojas_agendamentos?: LojaMenu[];
  lojas_instalacoes?: LojaMenu[];
  /** As lojas onde a pessoa cria OS (o campo Loja da Nova OS). */
  lojas_criar_os?: LojaMenu[];
  /** Verdadeiro quando a sessão está emprestada (ver `verComo`). */
  vendo_como?: boolean;
  /** Quem emprestou a sessão — só vem no modo "ver como". */
  admin_nome?: string | null;
}

interface AuthContextValue {
  usuario: Usuario | null;
  carregando: boolean;
  login: (usuario: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  recarregar: () => Promise<void>;
  verComo: (usuarioId: number) => Promise<void>;
  voltarAoMeuAcesso: () => Promise<void>;
  /**
   * A pessoa alcança esta permissão? É a MESMA string que trava a rota no
   * backend — se um item de menu precisa de uma regra que a rota não aplica,
   * a regra está errada, não o menu.
   */
  pode: (permissao: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  // Verifica a sessão existente uma vez, ao carregar o app.
  useEffect(() => {
    api
      .get<Usuario>('/auth/me')
      .then(setUsuario)
      .catch(() => setUsuario(null))
      .finally(() => setCarregando(false));
  }, []);

  const login = async (nomeUsuario: string, senha: string) => {
    const dados = await api.post<Usuario>('/auth/login', {
      usuario: nomeUsuario,
      senha,
    });
    setUsuario(dados);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* mesmo se falhar no servidor, derruba a sessão local */
    }
    setUsuario(null);
  };

  // Re-busca os dados do usuário logado (após editar o perfil/foto).
  const recarregar = async () => {
    try {
      setUsuario(await api.get<Usuario>('/auth/me'));
    } catch {
      /* ignora; mantém o que tem */
    }
  };

  /**
   * Empresta a sessão: o admin passa a ver o app pelos olhos de outra pessoa,
   * sem pedir a senha dela. É SÓ LEITURA — o servidor recusa qualquer escrita
   * enquanto o modo está ligado.
   *
   * `location.replace`, e não navegação do React Router: o app inteiro precisa
   * remontar com o usuário novo. Permissões, menu e dados em cache foram
   * calculados para outra pessoa, e conservá-los mostraria uma mistura das
   * duas — que é justo o que este modo existe para não fazer.
   */
  const verComo = async (usuarioId: number) => {
    await api.post<Usuario>('/auth/ver-como', { usuario_id: usuarioId });
    window.location.replace('/inicio');
  };

  const voltarAoMeuAcesso = async () => {
    await api.post<Usuario>('/auth/voltar');
    window.location.replace('/inicio');
  };

  // Admin alcança tudo por definição — a mesma regra do backend, para o menu
  // não depender de o seed ter listado permissão por permissão.
  const pode = (permissao: string) =>
    !!usuario && (usuario.admin || (usuario.permissoes ?? []).includes(permissao));

  return (
    <AuthContext.Provider
      value={{
        usuario,
        carregando,
        login,
        logout,
        recarregar,
        verComo,
        voltarAoMeuAcesso,
        pode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  }
  return ctx;
}
